import { useQuery } from "@msuite/ui/query";
import { type ColumnDef, DataTable } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { guardString } from "@msuite/utils/strings";
import type { MeasurementAccessType, ProjectMeasurement } from "@msuite/utils/types";
import { useParams } from "react-router";
import { client } from "@/hono";
import { MeasurementContext } from "./context";

/** Props Interface */
interface MeasurementProps {
	type: MeasurementAccessType;
	columns: ColumnDef<ProjectMeasurement, any>[];
	menu?: null | React.ReactNode | (() => React.ReactNode);
}

export function Measurement({ type, columns, menu = null }: MeasurementProps) {
	const params = useParams();
	const projectId = params.projectId;

	const { data, isLoading, refetch, queryKey } = useQuery({
		queryKey: ["measurement", guardString(projectId)],
		queryFn: async () => {
			if (!projectId) throw new Error("Project ID is required");
			const response = await client.measurements[":projectId"].$get({
				param: { projectId },
			});
			const data = await responseHandler(response);
			return data.measurement;
		},
		enabled: !!projectId,
	});

	/** Render */
	return (
		<MeasurementContext.Provider value={{ type }}>
			<DataTable
				data={guardArray(data)}
				columns={columns}
				persistenceKey="measurement-table"
				isLoading={isLoading}
				queryKey={queryKey}
				refetch={refetch}
				menu={menu}
				enableRowSelection
				enableMultiRowSelection
				initialColumnFilter={[
					{
						id: "isClosed",
						value: false,
					},
				]}
				initialPinningState={{
					start: ["date"],
					end: ["isClosed", "notes"],
				}}
			/>
		</MeasurementContext.Provider>
	);
}
